'use client';

import { useEffect, useRef } from 'react';
import Env, {
	syncGetCurrentSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import { getRpcToUse } from 'src/utils/rpcUtils/rpcUtils_api';
import useLiquidationAlerts from './Liquidations/useLiquidationAlerts';
import useLiquidationWarningToasts from './Liquidations/useLiquidationWarningToasts';
import use24hMarketDataFromApi from './use24hMarketDataFromApi';
import useSyncBorrowLendBalances from './useSyncBorrowLendBalances';
import useClearStaleNotifications from './useClearStaleNotifications';
import { useKeepCurrentRpcHealthInSync } from './useCurrentRpcHealth';
import { useSyncAllRpcLatencies } from './useRpcLatency';
import { useSyncAccountsBeingLiquidated } from './Liquidations/useAccountsBeingLiquidated';
import { useSyncSwiftServerHealth } from './swift/useSwiftServerHealthcheck';

import { useUpdateConnection } from './velocity-actions/connection/useUpdateConnection';
import useSyncVelocityStatus from './useSyncVelocityStatus';
import useVelocityMarketEventListener from './velocityEvents/useVelocityMarketEventListener';
import useGeolocation from './useGeolocation';
import useKeepWalletsInSync from './useKeepWalletsInSync';
import useMarketHistory from './useMarketHistory';
import usePredictedFunding from './usePredictedFunding';
import usePerpMarketInfoSync from './usePerpMarketInfoSync';
import { useSyncCurrentMarketInfoDisplay } from './useMarketInfoDisplayData';
import useSyncOracleDataWithRpc from './useSyncOracleDataWithRpc';
import useSolBalance from './useSolBalance';
import useSyncWithAllMarketsInfo from './useSyncWithAllMarketsInfo';
import useUserStatsAccount from './useUserStatsAccount';
import useListenForNewDeploys from './useListenForNewDeploys';
import useIdlePollingRateSwitcher from './useIdlePollingSwitcher';
import useSyncSavedSubaccount from './useSyncSavedSubaccount';
import useCountConsoleErrors from './utils/useCountConsoleErrors';
import useDevInvariants from './dev/useDevInvariants';
import usePostHogGlobalEvents from './posthog/usePostHogGlobalEvents';
import useDocumentTitle from './useDocumentTitle';
import useAutomatedTxnsListener from './useAutomatedTxnsListener';
import useSyncMarketDataState from './dlob/useSyncMarketDataState';
import useWalletHooks from './useWalletHooks';
import { useHydrateVelocityStoreFromLocalStorage } from './useHydrateVelocityStoreFromLocalStorage';
import { useSyncAccountSpotBalances } from './useSyncAccountSpotBalances';
import useSyncPriorityFeeStore from '../providers/priorityFees/useSyncPriorityFeeStore';
import useOptimizedAccountLoader from './useOptimizedAccountLoader';
import useSyncAccountExists from './useSyncAccountExists';
import useSyncWindowSizeStore from './useSyncWindowSizeStore';
import useManageVelocityEventSubscriptions from './velocityEvents/useManageVelocityEventSubscriptions';
import useDevActions from './useDevActions';
import useSelfFilling from './useSelfFilling';
import useSyncVelocityClock from './useSyncChainClock';
import useSyncFeatureFlags from 'src/stores/featureFlag/useSyncFeatureFlags';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useAppEventEmitter from './useAppEventEmitter';
import usePostHogCapture from './posthog/usePostHogCapture';
import useDownloadRequests from './useDownloadRequests';
import useRouterConfig from './useRouterConfig';
import { useSyncTokenAccountsStore } from '../stores/tokenAccounts/useSyncTokenAccountsStore';
import useNavigationState from 'src/components/Navigation/hooks/useNavigationState';
import useSyncEventsSubscriptionState from './velocityEvents/useSyncEventsSubscriptionState';
import { useSyncVaultClient } from 'src/stores/vaultsStore/useSyncVaultClient';
import { useSyncDepositInputStore } from './deposits/useSyncDepositInputStore';
import { useGlobalHotkeySetup } from './hotkeys/useGlobalHotkeySetup';
import useAlertNotificationListener from 'src/components/Toasts/AlertNotifications/hooks/useAlertNotificationListener';
import usePostHogSurveys from 'src/hooks/posthog/usePostHogSurveys';
import { useSlotSubscription } from 'src/hooks/subscriptions/useSlotSubscription';
import useVelocityGlobalEventSubscribers from './velocityEvents/useVelocityGlobalEventSubscribers';
import { useVaultConfigs } from './query/vaults/base/useVaultConfigs';

import useSyncMarketSettings from './globalSyncs/marketSettings/useSyncMarketSettings';
import useFunXyzDepositWatcher from './useFunXyzDepositWatcher';
import useEmitPositionFillDeltas from './velocityEvents/useEmitPositionFillDeltas';
import { useSyncPrivy } from './globalSyncs/wallet/useSyncPrivy';
import useConnectE2eWallet from './globalSyncs/wallet/useConnectE2eWallet';
import useCleanupLargeLocalStorage from './useCleanupLargeLocalStorage';
import useTopMakerAlertNotification from 'src/hooks/useTopMakerAlertNotification';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';

// NOTE: ^ move all global sync hooks to the /globalSyncs folder

/**
 * Nextjs is really annoying with its server-side rendering, and most of the app setup needs to happen in hooks, otherwise the server tries to access things like "window" which is only available in the browser.
 * @returns
 */
const useAppSetup = () => {
	// PERFORMANCE NOTE : THIS HOOK MAKE THE OUTER HOOK RENDER MUTLIPLE TIMES WHEN ON THE /dev/dev route, but not the trade page
	const get = useVelocityStore((s) => s.get);
	const getAccountStore = useVelocityAccountsStore((s) => s.get);
	const appEventEmitter = useAppEventEmitter();
	const { captureEvent } = usePostHogCapture();
	const { updateConnection } = useUpdateConnection();
	const didBootstrapConnection = useRef(false);

	useEffect(() => {
		// @ts-ignore
		window.velocity_dev = { getStore: get, getAccountStore: getAccountStore };
	}, []);

	// Initial setup of app-related things
	useEffect(() => {
		// `updateConnection` is async and builds a fresh VelocityClient. Running it
		// twice (StrictMode remounts mount effects in dev) races two connection
		// setups against each other, so bootstrap it exactly once per app load.
		if (didBootstrapConnection.current) return;

		const rpcToUse = getRpcToUse();

		const usingDevnetOverride = syncGetCurrentSettings()?.devnetOverride;

		if (usingDevnetOverride) {
			notify({
				type: 'info',
				message: 'Using devnet settings',
			});
		}

		const currentSettings = syncGetCurrentSettings();

		if (!currentSettings) return;

		didBootstrapConnection.current = true;

		// initialize clearing house and connection
		updateConnection(
			rpcToUse,
			currentSettings.accountSubscriberType,
			Env.sdkEnv,
			'initializing'
		);
	}, []);

	useEffect(() => {
		TransactionErrorHandler.setAppEventEmitter(appEventEmitter);
		TransactionErrorHandler.setCaptureEvent(captureEvent);
	}, [appEventEmitter, captureEvent]);

	useRouterConfig();
	useHydrateVelocityStoreFromLocalStorage();
	useSlotSubscription();
	useCleanupLargeLocalStorage();

	// These hooks now handle route-based optimization internally
	useListenForNewDeploys();
	useVelocityMarketEventListener();
	use24hMarketDataFromApi();
	useMarketHistory();
	useSolBalance();
	useGeolocation();
	useSyncVelocityStatus();
	useSyncOracleDataWithRpc();

	useSyncBorrowLendBalances();
	usePredictedFunding();
	usePerpMarketInfoSync();
	useSyncCurrentMarketInfoDisplay();
	useLiquidationAlerts();
	useLiquidationWarningToasts();
	useKeepCurrentRpcHealthInSync();
	useSyncAllRpcLatencies();
	useSyncAccountsBeingLiquidated();
	useSyncSwiftServerHealth();
	useKeepWalletsInSync();
	useUserStatsAccount();
	useIdlePollingRateSwitcher();
	useSyncSavedSubaccount();
	useCountConsoleErrors();
	useDevInvariants();
	usePostHogGlobalEvents();
	usePostHogSurveys();
	useDocumentTitle();
	useSyncPriorityFeeStore();
	useOptimizedAccountLoader();
	useAutomatedTxnsListener();
	useWalletHooks();
	useSyncAccountSpotBalances();
	useSyncAccountExists();
	useSyncWindowSizeStore();
	useManageVelocityEventSubscriptions();
	useSelfFilling();
	useSyncFeatureFlags();
	useDownloadRequests();
	useSyncTokenAccountsStore();
	useNavigationState();
	useSyncEventsSubscriptionState();
	useSyncVaultClient();
	useVelocityGlobalEventSubscribers();
	useVaultConfigs();
	useGlobalHotkeySetup();
	useSyncMarketSettings();
	useFunXyzDepositWatcher();

	/// Wallet
	useSyncPrivy();
	useConnectE2eWallet();

	// PERFORMANCE NOTE : THESE HOOKS CAUSE THIS HOOK TO RUN ON A LOOP (on the trade page - for some reason it still loops on other pages though e.g. on route /dev/dev )
	useSyncMarketDataState();
	useSyncWithAllMarketsInfo();
	useSyncVelocityClock();

	// Toasts / Notifications
	useClearStaleNotifications();
	useAlertNotificationListener();
	useEmitPositionFillDeltas();
	useTopMakerAlertNotification();

	// sync stuff after remote configs loaded
	useSyncDepositInputStore();

	// Dev Stuff
	useDevActions();

	return;
};

export default useAppSetup;
